/**
 * Portal Push Subscription API
 *
 * Athlete-side push subscriptions. Subscribe and unsubscribe both go through
 * the scraper-api (Railway), which validates the portal session and writes
 * portal_push_subscriptions with admin rights. The row is keyed by endpoint
 * and toggled with is_active, so a device that turns push off and on again
 * keeps one row.
 */

import { SCRAPER_API_URL as _BASE } from '../../lib/config'
import { getClientSession } from '../clientAuth'

// Normalise URL: must start with https:// in prod
const SCRAPER_API_URL = _BASE && !_BASE.startsWith('http') ? `https://${_BASE}` : _BASE

export const VAPID_PUBLIC_KEY = (import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined) || ''
export const PUSH_ENDPOINT_KEY = 'reppr_push_endpoint'
export const PUSH_NOT_CONFIGURED =
  'Push is not configured on this deployment (VITE_VAPID_PUBLIC_KEY is unset). Ask your agency.'

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const output = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i++) output[i] = rawData.charCodeAt(i)
  return output
}

export function applicationServerKey(): ArrayBuffer {
  return urlBase64ToUint8Array(VAPID_PUBLIC_KEY).buffer as ArrayBuffer
}

/** True when the browser's subscription was created with our VAPID key. */
export function subscriptionKeyMatches(sub: PushSubscription): boolean {
  const key = sub.options?.applicationServerKey
  if (!key) return true // Older browsers do not expose it; assume ours.
  const ours = urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
  const theirs = new Uint8Array(key)
  if (ours.length !== theirs.length) return false
  for (let i = 0; i < ours.length; i++) if (ours[i] !== theirs[i]) return false
  return true
}

function portalHeaders(token: string) {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
}

async function readError(res: Response, fallback: string): Promise<string> {
  const data = await res.json().catch(() => ({})) as { error?: string }
  return data.error || `${fallback} (${res.status})`
}

/** POST the subscription to Railway and remember its endpoint locally. */
export async function saveSubscriptionToServer(sub: PushSubscription, token: string): Promise<void> {
  if (!SCRAPER_API_URL) throw new Error('Notification service not configured')
  const json = sub.toJSON()
  const keys = json.keys as { p256dh?: string; auth?: string } | undefined
  if (!json.endpoint || !keys?.p256dh || !keys?.auth) {
    throw new Error('Browser returned an incomplete push subscription')
  }
  const res = await fetch(`${SCRAPER_API_URL}/api/portal/push-subscribe`, {
    method: 'POST',
    headers: portalHeaders(token),
    body: JSON.stringify({ endpoint: json.endpoint, p256dh: keys.p256dh, auth: keys.auth }),
  })
  if (!res.ok) throw new Error(await readError(res, 'Could not save the subscription'))
  localStorage.setItem(PUSH_ENDPOINT_KEY, json.endpoint)
}

/**
 * Check if push notifications are supported in this browser.
 */
export function isPushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

/**
 * Get current permission state: 'default' | 'granted' | 'denied'
 */
export function getPushPermission(): NotificationPermission {
  if (!('Notification' in window)) return 'denied'
  return Notification.permission
}

/**
 * Request notification permission. Must be called from a user gesture.
 */
export async function requestPushPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) return 'denied'
  return Notification.requestPermission()
}

/**
 * Check if the athlete is currently subscribed.
 */
export async function isSubscribed(): Promise<boolean> {
  if (!isPushSupported()) return false
  try {
    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.getSubscription()
    return sub !== null
  } catch {
    return false
  }
}

/**
 * Subscribe the athlete's browser to push notifications.
 */
export async function subscribePortalPush(): Promise<{ success: boolean; error?: string }> {
  if (!isPushSupported()) {
    return { success: false, error: 'Push notifications are not supported in this browser' }
  }
  if (!VAPID_PUBLIC_KEY) {
    return { success: false, error: PUSH_NOT_CONFIGURED }
  }
  const token = getClientSession()?.token
  if (!token) {
    return { success: false, error: 'Not authenticated' }
  }
  if (!SCRAPER_API_URL) {
    return { success: false, error: 'Notification service not configured' }
  }

  try {
    const permission = await requestPushPermission()
    if (permission !== 'granted') {
      return { success: false, error: 'Notification permission denied' }
    }

    // navigator.serviceWorker.ready never rejects; add a 10s timeout so we
    // surface a clear error instead of hanging forever on a fresh tab.
    const reg = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error('Service worker not ready. Try refreshing the page.')),
          10_000,
        )
      ),
    ])
    let sub = await reg.pushManager.getSubscription()
    if (sub && !subscriptionKeyMatches(sub)) {
      await sub.unsubscribe()
      sub = null
    }
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey(),
      })
    }

    await saveSubscriptionToServer(sub, token)
    return { success: true }
  } catch (err: any) {
    return { success: false, error: err.message || 'Failed to enable notifications' }
  }
}

/**
 * Unsubscribe the athlete's browser. The server row is deactivated first;
 * if that fails the browser subscription is left in place and the error is
 * returned, so the UI never reports "off" while the server would still send.
 */
export async function unsubscribePortalPush(): Promise<{ success: boolean; error?: string }> {
  try {
    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.getSubscription()
    if (!sub) {
      localStorage.removeItem(PUSH_ENDPOINT_KEY)
      return { success: true }
    }
    const token = getClientSession()?.token
    if (!token) return { success: false, error: 'Not authenticated' }
    if (!SCRAPER_API_URL) return { success: false, error: 'Notification service not configured' }

    const res = await fetch(`${SCRAPER_API_URL}/api/portal/push-unsubscribe`, {
      method: 'POST',
      headers: portalHeaders(token),
      body: JSON.stringify({ endpoint: sub.endpoint }),
    })
    // 404 means the server never had this endpoint; nothing to deactivate.
    if (!res.ok && res.status !== 404) {
      return { success: false, error: await readError(res, 'Could not disable notifications') }
    }

    await sub.unsubscribe()
    localStorage.removeItem(PUSH_ENDPOINT_KEY)
    return { success: true }
  } catch (err: any) {
    return { success: false, error: err.message || 'Failed to disable notifications' }
  }
}
